import { createSlice } from '@reduxjs/toolkit';

import { MAX_SELECTED_SYMBOLS } from '@/constants/constants';
import type { SymbolKey } from '@/entities/symbol';
import { getUniqueSymbols, symbolKey } from '@/entities/symbol';

import type { PayloadAction } from '@reduxjs/toolkit';

type SymbolSelectorState = {
  symbols: SymbolKey[];
  previousSymbols: SymbolKey[] | null;
  limitReached: boolean;
  isForwardMode: boolean; // only enabled on Dashboard when FWD is selected
  isDashboardAppSelector: boolean; // true when used in Dashboard app context
};

const initialState: SymbolSelectorState = {
  symbols: [],
  previousSymbols: null,
  limitReached: false,
  isForwardMode: false,
  isDashboardAppSelector: false
};

type SymbolAction = {
  symbol: SymbolKey;
};

type SymbolArrayAction = {
  symbols: SymbolKey[];
};

const buildKeySet = (symbols: SymbolKey[], isForwardMode: boolean) =>
  new Set(symbols.map(s => symbolKey(s, isForwardMode)));

const filterByKey = (symbols: SymbolKey[], excludeKeys: Set<string>, isForwardMode: boolean) =>
  symbols.filter(s => !excludeKeys.has(symbolKey(s, isForwardMode)));

const updateLimitReached = (state: SymbolSelectorState) => {
  state.limitReached = state.symbols.length >= MAX_SELECTED_SYMBOLS;
};

const symbolSelectorSlice = createSlice({
  name: 'symbolSelector',
  initialState,
  reducers: {
    setSymbols: (state, action: PayloadAction<SymbolKey[]>) => {
      state.symbols = action.payload;
    },
    addSymbol: (state, { payload }: PayloadAction<SymbolAction>) => {
      const { symbol } = payload;
      const keys = buildKeySet(state.symbols, state.isForwardMode);
      if (keys.has(symbolKey(symbol, state.isForwardMode))) return;

      if (state.symbols.length >= MAX_SELECTED_SYMBOLS) {
        state.limitReached = true;
        return;
      }

      state.symbols = [symbol, ...state.symbols];
    },

    addSymbols: (state, { payload }: PayloadAction<SymbolArrayAction>) => {
      const keys = buildKeySet(state.symbols, state.isForwardMode);
      const toAdd: SymbolKey[] = [];
      for (const s of payload.symbols) {
        const k = symbolKey(s, state.isForwardMode);
        if (keys.has(k)) continue;
        if (state.symbols.length + toAdd.length >= MAX_SELECTED_SYMBOLS) {
          state.limitReached = true;
          break;
        }
        keys.add(k);
        toAdd.push(s);
      }
      if (toAdd.length) {
        state.symbols = [...state.symbols, ...toAdd];
      }
    },

    deleteSymbol: (state, { payload }: PayloadAction<SymbolAction>) => {
      const k = symbolKey(payload.symbol, state.isForwardMode);
      state.symbols = state.symbols.filter(s => symbolKey(s, state.isForwardMode) !== k);
      updateLimitReached(state);
    },

    deleteSymbols: (state, { payload }: PayloadAction<{ symbols: SymbolKey[] }>) => {
      const deleteSet = buildKeySet(payload.symbols, state.isForwardMode);
      state.symbols = filterByKey(state.symbols, deleteSet, state.isForwardMode);
    },

    reorderSymbols: (state, { payload }: PayloadAction<SymbolArrayAction>) => {
      state.symbols = payload.symbols;
    },

    toggleSymbol: (state, { payload }: PayloadAction<SymbolAction>) => {
      const { symbol } = payload;
      const k = symbolKey(symbol, state.isForwardMode);
      const keys = buildKeySet(state.symbols, state.isForwardMode);

      if (keys.has(k)) {
        state.symbols = state.symbols.filter(s => symbolKey(s, state.isForwardMode) !== k);
        updateLimitReached(state);
      } else if (state.symbols.length >= MAX_SELECTED_SYMBOLS) {
        state.limitReached = true;
      } else {
        state.symbols = [symbol, ...state.symbols];
      }
    },

    toggleSymbolsArray: (state, { payload }: PayloadAction<SymbolArrayAction>) => {
      const existingKeys = buildKeySet(state.symbols, state.isForwardMode);

      const toRemove = new Set<number>();
      const toAdd: SymbolKey[] = [];

      const indexByKey = new Map<string, number>();
      for (let i = 0; i < state.symbols.length; i++) {
        indexByKey.set(symbolKey(state.symbols[i], state.isForwardMode), i);
      }

      for (const sym of payload.symbols) {
        const k = symbolKey(sym, state.isForwardMode);
        if (existingKeys.has(k)) {
          const idx = indexByKey.get(k);
          if (idx != null) toRemove.add(idx);
        } else {
          if (state.symbols.length - toRemove.size + toAdd.length >= MAX_SELECTED_SYMBOLS) {
            state.limitReached = true;
            continue;
          }
          toAdd.push(sym);
        }
      }

      if (toRemove.size || toAdd.length) {
        const next: SymbolKey[] = [];
        for (let i = 0; i < state.symbols.length; i++) {
          if (!toRemove.has(i)) next.push(state.symbols[i]);
        }
        state.symbols = [...toAdd, ...next];
        updateLimitReached(state);
      }
    },

    overrideSymbols: (state, { payload }: PayloadAction<SymbolArrayAction>) => {
      state.previousSymbols = state.symbols;
      state.symbols = payload.symbols;
      updateLimitReached(state);
    },

    restoreSymbols: state => {
      if (state.previousSymbols !== null) {
        state.symbols = state.previousSymbols;
        state.previousSymbols = null;
        updateLimitReached(state);
      }
    },

    clearSelection: state => {
      state.symbols = [];
      state.limitReached = false;
    },

    dismissLimit: state => {
      state.limitReached = false;
    },

    setForwardMode: (state, action: PayloadAction<boolean>) => {
      const nextMode = action.payload;
      if (state.isForwardMode === nextMode) return;
      state.isForwardMode = nextMode;
      state.symbols = getUniqueSymbols(state.symbols, state.isForwardMode);
    },
    setIsDashboardAppSelector: (state, action: PayloadAction<boolean>) => {
      state.isDashboardAppSelector = action.payload;
    }
  }
});

export const {
  setSymbols,
  addSymbol,
  addSymbols,
  deleteSymbol,
  clearSelection,
  dismissLimit,
  deleteSymbols,
  reorderSymbols,
  toggleSymbol,
  toggleSymbolsArray,
  setForwardMode,
  setIsDashboardAppSelector,
  overrideSymbols,
  restoreSymbols
} = symbolSelectorSlice.actions;

export default symbolSelectorSlice.reducer;
